import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFilters } from '../hooks/useFilters';
import AiSearchBar from '../components/home/AiSearchBar';
import { practiceAreaIcons } from '../utils/icons';
import heroImage from '../assets/images/hero-image.png';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common/Toast';
import { FaSearchLocation, FaUserPlus, FaSignInAlt, FaArrowRight, FaRobot } from 'react-icons/fa';
import AppSidebar from '../components/common/AppSidebar';
import FAQ from '../components/common/FAQ';
import '../styles/layouts/HomePage.scss';

const HomePage = () => {
  const { cities, practiceAreas } = useFilters();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedPracticeArea, setSelectedPracticeArea] = useState('');
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { ToastContainer } = useToast();
  
  // FAQ data
  const faqItems = [
    {
      question: "What is this AI tool?",
      answer: "This AI is trained on UK legal information. It helps you:\n\n• Understand your legal issue\n• Identify what type of help you may need\n• Guide you toward next steps\n\nIt works via voice or chat and is available 24/7."
    },
    {
      question: "Is this legal advice?",
      answer: "No.\n\nThe AI gives general legal information, not advice. It doesn't replace a qualified solicitor. You should always speak to a solicitor for legal advice tailored to your situation."
    },
    {
      question: "What happens in the discovery call?",
      answer: "You speak to the AI agent (by voice or chat). It will:\n\n• Ask you questions to understand your issue\n• Explain what area of law is involved\n• Suggest next steps or refer you to a solicitor\n\nIt's a triage call — not a full consultation."
    },
    {
      question: "Is a solicitor involved?",
      answer: "Not at this stage.\n\nIf the AI identifies that you need legal advice, it can:\n\n• Connect you to a qualified solicitor\n• Help you book a follow-up call with a legal professional"
    },
    {
      question: "What can I ask about?",
      answer: "You can ask about common legal topics like:\n\n• Contracts\n• Debt\n• Landlord or tenant issues\n• Divorce or family matters\n• Employment rights\n• Immigration\n\nThe AI can help you understand what kind of legal help you might need."
    },
    {
      question: "What if the AI can't help me?",
      answer: "The AI will:\n\n• Tell you if it doesn't have the answer\n• Suggest where you might get help\n• Offer to connect you with a solicitor if appropriate"
    },
    {
      question: "How much does this cost?",
      answer: "The AI discovery call is free.\n\nIf you speak to a solicitor later, they will explain any fees before work begins."
    },
    {
      question: "Is my information safe?",
      answer: "Yes.\n\nWe follow UK GDPR rules. Your data is encrypted and only shared with legal professionals if you choose to be referred."
    },
    {
      question: "Can I trust the AI?",
      answer: "The AI is trained on verified legal sources. It's designed to help you understand your issue — not solve it. It will always encourage you to speak to a regulated legal professional for proper advice."
    }
  ];

  // Set data-page attribute on body for CSS targeting
  useEffect(() => {
    document.body.setAttribute('data-page', 'home');
    document.body.classList.add('ai-search-active');
    
    return () => {
      document.body.removeAttribute('data-page');
      document.body.classList.remove('ai-search-active');
    };
  }, []);

  const handleSearch = () => {
    navigate('/search');
  };

  const handlePracticeAreaClick = (area) => {
    navigate('/search');
  };

  // Show loading spinner if auth is still being checked
  if (loading) return <div className="loading-spinner">Loading...</div>;
  
  return (
    <>
      <SEO 
        fallbackTitle="Zahoot - Find Legal Services in the UK"
        fallbackDescription="Find and connect with legal service providers across the United Kingdom. Browse solicitors, law firms, and legal professionals in your area."
      />
      <ToastContainer />
      <div className="home-page">
        {/* Sidebar Navigation */}
        <AppSidebar />
        
        <main className="home-page__main">
          <div className="max-w-6xl w-full px-4 lg:right-9 sm:px-6 lg:px-8 mx-auto z-10 relative">
            
            {/* Hero Section with Gradient Background */}
            <div className="text-center mb-12 z-10 relative">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
                Find the <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B]">Perfect</span> Legal Partner for your legal needs
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Connect with qualified legal professionals, compare reviews, and make informed decisions about your legal representation
              </p>
            </div>
            
            {/* AI-powered Search Bar */}
            <AiSearchBar />
            
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center mt-10 z-10 relative">
              <Link 
                to="/search" 
                className="btn btn--primary btn--lg flex items-center"
                style={{background: 'linear-gradient(92.68deg, #D80364 0%, #F0654B 100%)'}}
              >
                <FaSearchLocation className="mr-2 text-xl" />
                Search Solicitors
              </Link>
              <Link 
                to="/signup" 
                className="btn btn--white btn--lg flex items-center"
              >
                <FaUserPlus className="mr-2 text-xl" />
                Create Account
              </Link>
              <Link 
                to="/signin" 
                className="btn btn--outline btn--lg flex items-center"
              >
                <FaSignInAlt className="mr-2 text-xl" />
                Sign In
              </Link>
              <a 
                href="https://legalbrain.zahoot.ai" 
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--white btn--lg flex items-center"
              >
                <FaRobot className="mr-2 text-xl" />
                Legal Brain AI
              </a>
            </div>
          
            {/* Features Section */}
            <div className="py-20 mt-20 z-10 relative">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">Why Choose Zahoot AI</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="p-6 bg-purple-900/50 rounded-xl shadow-sm transition-all duration-300 hover:shadow-lg">
                  <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-2 text-white">Comprehensive Search</h3>
                  <p className="text-gray-300 text-center">Find legal professionals filtered by location, specialty, and ratings.</p>
                </div>
                
                <div className="p-6 bg-purple-900/50 rounded-xl shadow-sm transition-all duration-300 hover:shadow-lg">
                  <div className="w-16 h-16 bg-pink-500/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-pink-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-2 text-white">Verified Reviews</h3>
                  <p className="text-gray-300 text-center">Read authentic reviews from real clients to make informed decisions.</p>
                </div>
                
                <div className="p-6 bg-purple-900/50 rounded-xl shadow-sm transition-all duration-300 hover:shadow-lg">
                  <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-2 text-white">Secure Platform</h3>
                  <p className="text-gray-300 text-center">Your data is protected with industry-standard security protocols.</p>
                </div>
                
                <div className="p-6 bg-purple-900/50 rounded-xl shadow-sm transition-all duration-300 hover:shadow-lg">
                  <div className="w-16 h-16 bg-green-500/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <FaRobot className="h-8 w-8 text-green-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-2 text-white">AI Voice Agent <span className="text-xs font-normal text-green-300">(coming soon)</span></h3>
                  <p className="text-gray-300 text-center">Interact with our advanced AI assistant to get quick answers to your legal questions.</p>
                </div>
              </div>
            </div>
            
            {/* FAQ Section */}
            <div className="py-20 mt-20 z-10 relative">
              <FAQ 
                title="Legal Support Assistant (AI-Powered)" 
                faqItems={faqItems} 
              />
            </div>
            
            {/* CTA Section */}
            <div className="py-20 mt-10 mb-0 z-10 relative">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to find your legal representation?</h2>
                <p className="text-xl text-gray-300 mb-10">Join thousands of users who've already found their perfect legal help.</p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/signup" 
                    className="btn btn--purple btn--lg"
                  >
                    Get Started Today
                  </Link>
                  <Link 
                    to="/search" 
                    className="btn btn--outline btn--lg"
                  >
                    Explore Solicitors
                  </Link>
                </div>
              </div>
            </div>

            {/* Legal Footer Section */}
            <div className="py-8 mt-10 border-t border-purple-800/30 z-10 relative">
              <div className="max-w-4xl mx-auto text-center">
                <p className="text-sm text-gray-400 mb-4">
                  © {new Date().getFullYear()} Zahoot. All rights reserved.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm">
                  <Link 
                    to="/privacy-policy" 
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                  <span className="hidden sm:inline text-gray-600">|</span>
                  <Link 
                    to="/terms-of-service" 
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Terms & Conditions
                  </Link>
                  <span className="hidden sm:inline text-gray-600">|</span>
                  <Link 
                    to="/cookie-policy" 
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Cookie Policy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default HomePage; 